/**
 * Data pipeline — the critical path for all incoming events.
 *
 * source.fetch() → normalize → translate (if non-English) → classify (keyword)
 * → entityResolver.resolve() → alertEngine.evaluate() → store.addEvent() → render
 *
 * Phase 2 hooks (entityResolver, alertEngine) are called but return empty results.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { OfflineEventRecord } from '@/storage/indexeddb';
import { appStore } from './store';
import { eventBus } from './events';
import { entityResolver } from '@/intelligence/entities/entity-resolver';
import { alertEngine } from '@/intelligence/alerts/alert-engine';
import { scheduleRefinement } from '@/intelligence/ai/classifier';
import { extractEntities } from '@/intelligence/analysis/entity-extraction';
import { stripHtml } from '@/utils/sanitize';
import { cacheEvents, evictOldEvents } from '@/storage/indexeddb';
import { translate, detectLanguageByScript } from '@/intelligence/ai/translator';

const MAX_EVENTS = 50_000;

/**
 * Maximum size for the known-IDs dedup set.
 * When exceeded, the oldest half is pruned (approximate LRU via insertion order).
 */
const MAX_KNOWN_IDS = MAX_EVENTS * 2;

/** Set of known event IDs — prevents duplicate events from accumulating across scheduler refreshes. */
const _knownIds = new Set<string>();

/** Maximum title length after sanitization (chars). */
const MAX_TITLE_LENGTH = 500;
/** Maximum description length after sanitization (chars). */
const MAX_DESCRIPTION_LENGTH = 2000;
/** Maximum tags per event. */
const MAX_TAGS = 20;

/**
 * Validate and sanitize a NormalizedEvent at the pipeline boundary.
 * Returns null if the event is malformed beyond recovery.
 * Strips HTML from title/description, clamps field lengths, validates coordinates.
 */
function validateEvent(event: NormalizedEvent): NormalizedEvent | null {
  if (!event || typeof event !== 'object') return null;
  if (typeof event.id !== 'string' || event.id.length === 0) return null;
  if (typeof event.source !== 'string' || event.source.length === 0) return null;
  if (typeof event.title !== 'string' || event.title.length === 0) return null;
  if (typeof event.timestamp !== 'string') return null;

  // Validate timestamp is parseable
  const ts = new Date(event.timestamp).getTime();
  if (!Number.isFinite(ts)) return null;

  // Sanitize text fields — strip any embedded HTML
  const title = stripHtml(event.title).slice(0, MAX_TITLE_LENGTH);
  const description = event.description
    ? stripHtml(event.description).slice(0, MAX_DESCRIPTION_LENGTH)
    : undefined;

  // Validate location coordinates if present
  let location = event.location;
  if (location) {
    if (
      typeof location.lat !== 'number' || !Number.isFinite(location.lat) ||
      typeof location.lng !== 'number' || !Number.isFinite(location.lng) ||
      location.lat < -90 || location.lat > 90 ||
      location.lng < -180 || location.lng > 180
    ) {
      location = undefined;
    }
  }

  // Clamp tags
  const tags = Array.isArray(event.tags)
    ? event.tags.filter(t => typeof t === 'string').slice(0, MAX_TAGS)
    : undefined;

  return {
    ...event,
    title,
    description,
    location,
    tags,
    _timestampMs: ts,
  };
}

/**
 * Batch depth counter. While > 0, addEvent accumulates locally instead of
 * updating the store. processBatch flushes once after all events are added.
 */
let _batchDepth = 0;

/** Events accumulated during the current batch. Flushed atomically at batch end. */
let _pendingBatchEvents: NormalizedEvent[] = [];

/**
 * Trim an events array to MAX_EVENTS, evicting the oldest 10%.
 * Also cleans up _knownIds for evicted events.
 * Returns a new array (never mutates the input).
 */
function trimEvents(events: NormalizedEvent[]): NormalizedEvent[] {
  if (events.length <= MAX_EVENTS) return events;
  const trimCount = Math.floor(MAX_EVENTS * 0.1);
  for (let i = 0; i < trimCount; i++) {
    _knownIds.delete(events[i].id);
  }
  return events.slice(trimCount);
}

/** Add an event to the store with bounded buffer. Skips duplicates by event ID. */
function addEvent(event: NormalizedEvent): boolean {
  // Deduplicate: skip events we've already ingested
  if (_knownIds.has(event.id)) return false;
  _knownIds.add(event.id);

  // Cap _knownIds: prune oldest half when set exceeds limit (Set preserves insertion order)
  if (_knownIds.size > MAX_KNOWN_IDS) {
    const pruneCount = Math.floor(MAX_KNOWN_IDS / 2);
    let i = 0;
    for (const id of _knownIds) {
      if (i++ >= pruneCount) break;
      _knownIds.delete(id);
    }
  }

  if (_batchDepth > 0) {
    // Batch mode: accumulate locally, don't touch store until flush
    _pendingBatchEvents.push(event);
  } else {
    // Single-event path: atomic immutable update
    const current = appStore.getState().events;
    const updated = trimEvents([...current, event]);
    appStore.setState({ events: updated });
  }
  return true;
}

/**
 * Flush pending batch events into the store as one atomic immutable update.
 * Creates a new array reference so subscribers detect the change.
 */
function flushEvents(): void {
  const pending = _pendingBatchEvents;
  _pendingBatchEvents = [];
  if (pending.length === 0) return;
  const current = appStore.getState().events;
  const updated = trimEvents([...current, ...pending]);
  appStore.setState({ events: updated });
}

/**
 * Keyword-based classifier. Assigns severity and confidence based on title/description.
 * Confidence scores reflect keyword match quality:
 * - critical keywords: 0.45 (strong signal, but keyword-only)
 * - high keywords: 0.40
 * - medium keywords: 0.35
 * - no match: 0.20 (low confidence = high priority for LLM refinement)
 * - source already set severity: 0.50 (trust source classification)
 */
function classifyByKeyword(event: NormalizedEvent): NormalizedEvent {
  const text = `${event.title} ${event.description ?? ''}`.toLowerCase();

  const criticalKeywords = ['explosion', 'attack', 'killed', 'missile', 'nuclear', 'war'];
  const highKeywords = ['military', 'sanctions', 'conflict', 'protest', 'earthquake'];
  const mediumKeywords = ['tensions', 'warning', 'alert', 'incident', 'fire'];

  let severity = event.severity;
  let confidence: number;

  if (severity) {
    // Source already assigned severity — trust it at moderate confidence
    confidence = 0.50;
  } else if (criticalKeywords.some(kw => text.includes(kw))) {
    severity = 'critical';
    confidence = 0.45;
  } else if (highKeywords.some(kw => text.includes(kw))) {
    severity = 'high';
    confidence = 0.40;
  } else if (mediumKeywords.some(kw => text.includes(kw))) {
    severity = 'medium';
    confidence = 0.35;
  } else {
    severity = 'info';
    confidence = 0.20;
  }

  return { ...event, severity, confidence };
}

export interface PipelineResult {
  event: NormalizedEvent;
  alerts: { id: string; ruleId: string; ruleName: string; severity: string }[];
}

/** Process a single event through the full pipeline. */
export async function processEvent(raw: NormalizedEvent): Promise<PipelineResult> {
  // Step 0: Validate and sanitize at the boundary
  const validated = validateEvent(raw);
  if (!validated) {
    eventBus.emit('pipeline:error', { stage: 'validate', error: `Rejected malformed event: ${raw?.id ?? 'unknown'}` });
    return { event: raw, alerts: [] };
  }

  // Step 0.5: Language detection & translation (async, cached)
  // Detect language from title. If non-English, translate title and description.
  if (!validated.language) {
    validated.language = detectLanguageByScript(validated.title);
  }
  if (validated.language && validated.language !== 'en' && validated.language !== 'unknown') {
    try {
      // Preserve original title for display
      validated.originalTitle = validated.title;
      validated.title = await translate(validated.title, 'en');
      if (validated.description) {
        validated.description = await translate(validated.description, 'en');
      }
      // Add language tag
      if (!validated.tags) validated.tags = [];
      if (!validated.tags.includes(`lang:${validated.language}`)) {
        validated.tags.push(`lang:${validated.language}`);
      }
      validated.tags.push('translated');
    } catch {
      // Translation failed — tag the event so UI can handle it
      if (!validated.tags) validated.tags = [];
      if (!validated.tags.includes('translation:failed')) {
        validated.tags.push('translation:failed');
      }
    }
  }

  // Step 1: Keyword classification (synchronous, zero latency)
  const classified = classifyByKeyword(validated);

  // Step 1.5: Entity extraction from entity registry (synchronous)
  try {
    const entityMatches = extractEntities(classified);
    if (entityMatches.length > 0) {
      classified.entities = entityMatches.map(m => ({
        entityId: m.entityId,
        name: m.name,
        type: m.type,
        confidence: m.confidence,
      }));
    }
  } catch (err) {
    eventBus.emit('pipeline:error', { stage: 'entity-extraction', error: String(err) });
  }

  // Step 2: Entity resolution (Phase 2 stub — returns empty)
  try {
    const entityMatches = await entityResolver.resolve(classified);
    if (entityMatches.length > 0) {
      classified.entities = entityMatches.map(m => ({
        entityId: m.entityId,
        name: m.name,
        type: m.type,
        confidence: m.confidence,
      }));
    }
  } catch (err) {
    eventBus.emit('pipeline:error', { stage: 'entity-resolve', error: String(err) });
  }

  // Step 3: Alert evaluation (Phase 2 stub — returns empty)
  let alerts: PipelineResult['alerts'] = [];
  try {
    const triggered = await alertEngine.evaluate(classified);
    alerts = triggered.map(a => ({
      id: a.id,
      ruleId: a.ruleId,
      ruleName: a.ruleName,
      severity: a.severity,
    }));
    for (const alert of triggered) {
      eventBus.emit('alert:triggered', alert);
    }
  } catch (err) {
    eventBus.emit('pipeline:error', { stage: 'alert-evaluate', error: String(err) });
  }

  // Step 4: Add to store (bounded buffer, dedup by event ID)
  const added = addEvent(classified);

  // Steps 5-6 only run for new events (not duplicates from scheduler refresh)
  if (added) {
    eventBus.emit('event:added', classified);
    scheduleRefinement();
  }

  return { event: classified, alerts };
}

/** Event types that are coordinate-only (no meaningful text to translate). */
const COORDINATE_ONLY_TYPES = new Set([
  'flight_position', 'vessel_position', 'earthquake', 'fire', 'volcano',
]);

/**
 * Process a batch of events. Flushes the store once after all events are added.
 * Coordinate-only events skip translation and are processed synchronously in bulk.
 * Text events are processed in parallel batches of 20 for translation.
 */
export async function processBatch(events: NormalizedEvent[]): Promise<PipelineResult[]> {
  _batchDepth++;
  const results: PipelineResult[] = [];
  try {
    // Split: coordinate-only events (fast path) vs text events (need translation)
    const fastEvents: NormalizedEvent[] = [];
    const textEvents: NormalizedEvent[] = [];
    for (const event of events) {
      if (COORDINATE_ONLY_TYPES.has(event.type)) {
        fastEvents.push(event);
      } else {
        textEvents.push(event);
      }
    }

    // Fast path: process coordinate-only events synchronously (no await needed)
    for (const event of fastEvents) {
      const validated = validateEvent(event);
      if (!validated) continue;
      const classified = classifyByKeyword(validated);
      addEvent(classified);
      results.push({ event: classified, alerts: [] });
    }

    // Text events: process in parallel batches of 20
    const BATCH_SIZE = 20;
    for (let i = 0; i < textEvents.length; i += BATCH_SIZE) {
      const chunk = textEvents.slice(i, i + BATCH_SIZE);
      const chunkResults = await Promise.allSettled(
        chunk.map((event) => processEvent(event)),
      );
      for (const r of chunkResults) {
        if (r.status === 'fulfilled') results.push(r.value);
      }
    }
  } finally {
    _batchDepth--;
  }
  // Flush once for the entire batch — one new array reference, one subscriber notification.
  if (events.length > 0) {
    flushEvents();
    eventBus.emit('event:batch', events);
  }
  return results;
}

// ── Event Persistence (warm start) ──

const PERSIST_DEBOUNCE_MS = 30_000;
const MAX_PERSIST_EVENTS = 10_000;
let _persistTimer: ReturnType<typeof setTimeout> | null = null;

/** Persist current events to IndexedDB for warm-start on next session. */
async function persistEvents(): Promise<void> {
  const events = appStore.getState().events;
  if (events.length === 0) return;

  // Take the most recent events (by timestamp), capped at 10k
  const sorted = events
    .slice()
    .sort((a, b) => (b._timestampMs ?? 0) - (a._timestampMs ?? 0))
    .slice(0, MAX_PERSIST_EVENTS);

  const now = Date.now();
  const records: OfflineEventRecord[] = sorted.map((ev) => ({
    id: ev.id,
    data: ev,
    storedAt: now,
  }));

  try {
    await cacheEvents(records);
    await evictOldEvents();
  } catch {
    // Fire-and-forget — don't block pipeline on persistence failure
  }
}

/** Schedule a debounced persistence save. Resets timer on each call. */
function schedulePersist(): void {
  if (_persistTimer) clearTimeout(_persistTimer);
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    persistEvents().catch(() => {});
  }, PERSIST_DEBOUNCE_MS);
}

// Deferred initialization — avoids TDZ errors from circular imports in production bundles.
// Called once from main.ts after all modules are loaded.
let _pipelineListenersRegistered = false;
export function initPipelineListeners(): void {
  if (_pipelineListenersRegistered) return;
  _pipelineListenersRegistered = true;

  // Auto-persist on every batch flush
  eventBus.on('event:batch', () => schedulePersist());

  // Flush immediately when tab is hidden (user navigating away / closing)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && appStore.getState().events.length > 0) {
        if (_persistTimer) clearTimeout(_persistTimer);
        _persistTimer = null;
        persistEvents().catch(() => {});
      }
    });
  }
}

/**
 * Register event IDs as known to the dedup set.
 * Called from boot sequence after restoring cached events,
 * so fresh fetches properly deduplicate against restored data.
 */
export function registerKnownIds(ids: string[]): void {
  for (const id of ids) {
    _knownIds.add(id);
  }
}
